import { Trans, useTranslation } from "react-i18next";
import { CornerDownLeft } from "lucide-react";

// Speed traces for one illustrative lap against the best lap (viewBox units).
const BEST_LAP =
  "40,42 80,34 120,30 150,36 170,95 190,78 220,50 250,40 268,88 290,70 320,44 345,40 362,96 378,86 400,62 430,40 455,36 480,100 505,78 540,48 575,36 590,34";
const THIS_LAP =
  "40,43 80,35 120,31 150,37 170,96 190,79 220,51 250,41 268,89 290,71 320,45 338,46 355,103 376,93 400,70 430,45 455,38 480,101 505,79 540,49 575,37 590,35";

function SpeedChart({ title }: { title: string }) {
  return (
    <svg
      viewBox="0 0 600 190"
      className="block h-auto w-full max-w-full"
      role="img"
      aria-label={title}
    >
      <g className="fill-gray-500 font-mono text-[9.5px]">
        <line x1="40" y1="20" x2="40" y2="150" stroke="currentColor" strokeOpacity=".18" />
        <line x1="40" y1="150" x2="590" y2="150" stroke="currentColor" strokeOpacity=".18" />
        <line x1="40" y1="63.3" x2="590" y2="63.3" stroke="currentColor" strokeOpacity=".08" strokeDasharray="2 4" />
        <line x1="40" y1="106.7" x2="590" y2="106.7" stroke="currentColor" strokeOpacity=".08" strokeDasharray="2 4" />
        <text x="34" y="24" textAnchor="end">240</text>
        <text x="34" y="67" textAnchor="end">160</text>
        <text x="34" y="110" textAnchor="end">80</text>
        <text x="34" y="153" textAnchor="end">0</text>
        <text x="40" y="172">0 m</text>
        <text x="170" y="172" textAnchor="middle">T1</text>
        <text x="268" y="172" textAnchor="middle">T2</text>
        <text x="372" y="172" textAnchor="middle">T3</text>
        <text x="480" y="172" textAnchor="middle">T4</text>
        <text x="590" y="172" textAnchor="end">3,200 m</text>
      </g>
      <rect x="340" y="20" width="62" height="130" className="fill-violet-400/10" />
      <polyline fill="none" className="stroke-gray-500" strokeWidth="2" strokeDasharray="4 4" strokeLinejoin="round" points={BEST_LAP} />
      <polyline fill="none" className="stroke-primary-400" strokeWidth="2" strokeLinejoin="round" points={THIS_LAP} />
      <circle cx="355" cy="103" r="4" className="fill-primary-400 stroke-gray-900" strokeWidth="2" />
      <text x="371" y="34" textAnchor="middle" className="fill-white font-mono text-[9.5px] font-semibold">
        −0.21 s
      </text>
    </svg>
  );
}

export default function AnalysisMock() {
  const { t } = useTranslation("pages");

  return (
    <div className="grid gap-3.5 sm:grid-cols-[1.4fr_1fr]">
      <div className="min-w-0 rounded-2xl border border-gray-800 bg-gray-900/60 p-3.5 pb-2.5">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h4 className="text-[13px] font-semibold text-white">
            {t("home.mock.analysis.chartTitle")}
          </h4>
          <div className="flex gap-3.5 font-mono text-[10.5px] text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 border-t-2 border-primary-400" />
              {t("home.mock.analysis.legendLap")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 border-t-2 border-dashed border-gray-500" />
              {t("home.mock.analysis.legendBest")}
            </span>
          </div>
        </div>
        <SpeedChart title={t("home.mock.analysis.chartAlt")} />
      </div>

      <div className="flex min-w-0 flex-col gap-2.5 rounded-2xl border border-gray-800 bg-gray-900/60 p-4">
        <div className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-gray-400">
          <span className="h-4 w-4 rounded-md bg-gradient-to-br from-primary-500 to-violet-400" />
          {t("home.mock.analysis.who")}
        </div>
        <div className="font-mono text-2xl font-semibold tabular-nums tracking-[-0.02em] text-white">
          −0.21 s
          <small className="ml-2 text-xs font-normal text-gray-400">
            {t("home.mock.analysis.deltaWhere")}
          </small>
        </div>
        <p className="text-[13.5px] text-gray-400">
          <Trans
            t={t}
            i18nKey="home.mock.analysis.insight"
            components={{ strong: <strong className="font-semibold text-white" /> }}
          />
        </p>
        <div className="mt-auto flex items-center justify-between gap-2 rounded-full border border-gray-800 px-3 py-2 text-xs text-gray-400">
          <span>{t("home.mock.analysis.ask")}</span>
          <CornerDownLeft className="h-3.5 w-3.5" />
        </div>
      </div>
    </div>
  );
}
